/**
 * PRESENCE FOLLOWS THE SHARE — Presenting for as long as MY screen is on the
 * wire, in a meeting or in a call.
 *
 * It reads the room's own roster rather than watching `getDisplayMedia`: the
 * local participant's `sharing` flag is the same signal the Share/Stop share
 * button already renders, so presence and the toolbar can never disagree, and
 * the ways a share ends that no button knows about — the browser's own "Stop
 * sharing" bar, the shared window being closed, the track dying on a reconnect
 * — all land here too because they all come back through the roster.
 *
 * No new listener: `MediaClient.subscribe` is the store subscription the whole
 * meeting tree already renders from.
 */
import { useEffect } from 'react';
import { presenceActions } from '@/features/presence/presenceStore';
import type { MediaClient } from '../media/contract';

/** The room's client once it exists — the shape `useMediaPreferences` carries. */
export interface SharePresenceRef {
  readonly current: MediaClient | null;
}

const sharingNow = (client: MediaClient): boolean =>
  client.getSnapshot().participants.some((participant) => participant.isLocal && participant.sharing);

export function useSharePresence(clientRef: SharePresenceRef): void {
  useEffect(() => {
    const client = clientRef.current;
    /* Pre-join has preferences but no room, and therefore nothing to report. */
    if (client === null) return undefined;

    /* Only transitions are published: the roster changes on every speaker and
       quality tick, and presence must not be rewritten several times a second. */
    let sharing: boolean | null = null;
    const sync = (): void => {
      const next = sharingNow(client);
      if (next === sharing) return;
      sharing = next;
      presenceActions.setFlags({ presenting: next, screenSharing: next });
    };

    sync();
    const unsubscribe = client.subscribe(sync);
    return () => {
      unsubscribe();
      /* Leaving the room ends the share whatever the last roster said. */
      presenceActions.setFlags({ presenting: false, screenSharing: false });
    };
  }, [clientRef]);
}
